import { gql, NetworkStatus } from '@apollo/client';
import { useTimeSettings } from '@mnd-frontend/contexts';
import {
  ImageSort,
  ImageStatusFilter,
  MaterialListSort,
  MaterialStatus,
  MaterialStatusFilter,
  MaterialType,
} from '@mnd-frontend/graphql-schema';
import { useStateWithOverwrite } from '@mnd-frontend/hooks';
import IconDashboard from '@mnd-frontend/icons/components/Dashboard';
import IconList from '@mnd-frontend/icons/components/List';
import IconStory from '@mnd-frontend/icons/components/Story';
import { toBoolean } from '@mnd-frontend/ts';
import {
  Avatar,
  Badge,
  Box,
  BoxBody,
  BoxFooter,
  Button,
  Card,
  ListItem,
  materialStatusToBadgeVariant,
  ResourceList,
  ResourceListItemBody,
  Select,
  Spinner,
  Tabs,
  Text,
  ToggleSwitch,
  Tooltip,
} from '@mnd-frontend/ui/publish/components';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import { media } from '@mnd-frontend/ui/publish/utils';
import {
  GREY_200,
  PURPLE_400,
  SPACING_1,
  SPACING_2,
  SPACING_3,
  SPACING_5,
  SPACING_6,
} from '@mnd-frontend/ui/shared/tokens';
import { capitalizeFirstLetter } from '@mnd-frontend/utils';
import {
  type ChangeEvent,
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import type { ImageAspectRatio } from '../../../components/MediaUploaders/constants';
import { useCurrentPressroom } from '../../../contexts/CurrentPressroomContext';
import { useGetPressroomListQuery, useMediaPickerQuery } from '../../../generated/hooks';
import type { ModalMediaFragment } from '../../../generated/types';
import { EMPTY_ARRAY } from '../../../lib/constants';
import IconTabs from '../../../modules/publish/components/ImageBankPage/components/IconTabs';
import { DocumentUploaderCell } from '../../../modules/publish/components/Uploaders/DocumentUploaderCell';
import { ImageUploaderCell } from '../../../modules/publish/components/Uploaders/ImageUploaderCell';
import { VideoUploaderCell } from '../../../modules/publish/components/Uploaders/VideoUploaderCell';
import { getMaterialStatus } from '../../../modules/publish/helpers/getMaterialStatus';
import MediaCard from '../../MediaCard';
import NoImagesFound from '../components/EmptyState/NoImagesFound';
import FilterSearch from '../components/FilterSearch';
import FilterSelect from '../components/FilterSelect';
import FiltersWrapper from '../components/FiltersWrapper';
import Footer from '../components/Footer';

gql`
  query MediaPicker(
    $pressroomId: ID!
    $type: MaterialType!
    $after: String
    $query: String
    $status: MaterialStatusFilter
    $sort: MaterialListSort
    $exclude: [Int!]
    $first: Int!
    $imageStatus: ImageStatusFilter
    $imageSort: ImageSort
    $isImageQuery: Boolean!
  ) {
    pressroom(id: $pressroomId) {
      id
      materialList: materialList2(
        first: $first
        types: [$type]
        query: $query
        status: $status
        sort: $sort
        after: $after
        exclude: $exclude
      ) @skip(if: $isImageQuery) {
        currentPage
        totalPages
        materials: nodes {
          id
          ...ModalMedia
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }

      images(
        first: $first
        status2: $imageStatus
        query: $query
        after: $after
        sortBy: $imageSort
      ) @include(if: $isImageQuery) {
        currentPage
        totalPages
        materials: nodes {
          id
          ...MediaPickerImage
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }

  fragment ModalMedia on Material {
    id
    header
    status
    scheduledFor
    publishedAt: publishedAt2
    containedInPressroom: pressroom {
      id
      name
    }
    ...MediaPickerImage
    ... on Document {
      preview: thumbnail(transformation: IMAGE_BANK_THUMBNAIL)
      resourceDocument {
        id
        downloadUrl
      }
    }
    ... on Video {
      resourceVideo {
        id
        processed
        downloadUrl
      }
      thumbnailUrl
    }
  }

  fragment MediaPickerImage on Image {
    id
    header
    status
    scheduledFor
    publishedAt: publishedAt2
    altText
    containedInPressroom: pressroom {
      id
      name
    }
    usageCount
    thumbnail: image {
      id
      resourceImageId
      cloudinaryId
      url(transformation: IMAGE_BANK_THUMBNAIL)
      previewUrl: url(transformation: MATERIAL_HEADER_PREVIEW)
      altText
      croppedUrl: url
      downloadUrl
    }
  }
`;

const materialTypeLabelKeys = {
  [MaterialType.Image]: 'publish_pr-edit_related-material_filter-images',
  [MaterialType.Video]: 'publish_pr-edit_related-material_filter-videos',
  [MaterialType.Document]: 'publish_pr-edit_related-material_filter-documents',
} as const;

type MediaTypes = MaterialType.Image | MaterialType.Video | MaterialType.Document;

export type SelectedMediaMaterial = {
  __typename: MediaTypes;
  id: string;
};

const SpinnerWrapper = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const UploaderWrapper = styled.div<{ $visible: boolean }>`
  display: ${props => (props.$visible ? 'block' : 'none')};
`;

const MaterialCardGrid = styled.div<{ $asGrid: boolean }>`
  ${props =>
    props.$asGrid &&
    css`
      padding: 1.5rem 0;
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(13.5rem, 1fr));
      gap: 1.5rem;
    `}
`;
const FillEmptySpace = styled.div`
  flex-shrink: 1;
  margin-top: 0;
  visibility: hidden;
`;

const ModalCenter = styled.div`
  min-height: 40vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TabsPadding = styled.div`
  background: #fff;

  > div > div {
    padding: 0 ${SPACING_3};
  }
  > div:first-child > div:first-child > div:last-child {
    border-bottom: 1px solid ${props => props.theme.colorBorderDividerDefault};
  }
`;

const StickyWrapper = styled.div`
  position: sticky;
  z-index: 3;
  top: 0;
  background: inherit;
  > div > div {
    padding: 1rem 0;
  }
`;

const Header = styled.header`
  padding: ${SPACING_3} ${SPACING_6} ${SPACING_1} ${SPACING_3};
  display: flex;
  flex-direction: column;
  gap: ${SPACING_1};
`;

const TabsWrapper = styled.div`
  display: flex;
  gap: ${SPACING_5};
  align-items: flex-end;
  justify-content: flex-end;
`;

const StyledFiltersWrapper = styled(FiltersWrapper)`
  > :first-child {
    flex: 2;
  }
  > * {
    flex: 1;
  }
  > ${TabsWrapper} {
    flex: none;
  }
`;

const BadgeWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: ${SPACING_1};
  align-items: center;
`;

const StyledListItem = styled(ListItem)<{ tabIndex?: '0' }>`
  ${props =>
    props.tabIndex &&
    css`
      cursor: pointer;

      &:hover {
        background-color: ${GREY_200};
      }

      &:focus,
      &:focus-within {
        outline: none;
        z-index: 1;
        position: relative;
        box-shadow: 0 0 0 3px ${PURPLE_400};
      }
    `}
`;

const Static = styled.div`
  flex-shrink: 0;
  display: inherit;
  align-items: inherit;
  line-height: 0;
`;

const Actions = styled.div`
  margin-left: 2rem;
`;

const LoadMoreWrapper = styled.div`
  margin-top: ${SPACING_2};
  display: flex;
  justify-content: center;
`;

const EmptyStateWrapper = styled.div`
  padding: 1.5rem 0;
  > div {
    width: 13.5rem;
    height: 12.5rem;

    ${media.lessThan('tablet')} {
      width: 100%;
    }
  }
`;

interface Common {
  title?: string;
  description?: string;
  searchPlaceholder?: string;
  exclude?: SelectedMediaMaterial;
  onlyOtherPressrooms?: boolean;
  onDismiss: () => void;
  selectableTypes?: MediaTypes[];
  showUploader?: boolean;
  showImageUploader?: boolean;
  isDisabled?: (m: ModalMediaFragment) => boolean;
  isPending?: (m: ModalMediaFragment) => boolean;
  imageAspectRatio?: ImageAspectRatio;
}

interface Controlled extends Common {
  isSelected: (m: ModalMediaFragment) => boolean;
  onSelect: (m: ModalMediaFragment) => void;
}

type Props = Common | Controlled;

type ExternalProps = Props & {
  show: boolean;
};
const CustomItem = memo(
  ({
    item,
    inline = false,
  }: {
    item: {
      id: string;
      title: string;
      avatar: ReactElement;
      actions: ReactElement;
      description: ReactElement;
      onClick?: () => void;
    };
    onClick: (id: string, event: ChangeEvent<HTMLInputElement>) => void;
    inline?: boolean;
  }) => {
    return (
      <StyledListItem
        $inline={inline}
        tabIndex={item.onClick ? '0' : undefined}
        onClick={item.onClick}
        onKeyDown={e => {
          if (e.key === 'Enter' && item.onClick) {
            item.onClick();
          }
        }}
      >
        {item.avatar && <Static>{item.avatar}</Static>}
        <ResourceListItemBody title={item.title} description={item.description} />
        {item.actions && <Actions>{item.actions}</Actions>}
      </StyledListItem>
    );
  },
);

const materialStatusToImageStatus = (status: MaterialStatusFilter | null) => {
  if (status === MaterialStatusFilter.Published) return ImageStatusFilter.Published;
  if (status === MaterialStatusFilter.Draft) return ImageStatusFilter.Draft;
  if (status === MaterialStatusFilter.Scheduled) return ImageStatusFilter.Scheduled;
  return null;
};

const MediaPickerContent = (props: Props) => {
  const [t] = useTranslation();
  const { showFailureToast } = useToasts();
  const { sourceId, pressroomId: currentPressroomId } = useCurrentPressroom();

  const {
    onDismiss,
    showUploader = true,
    showImageUploader = false,
    onlyOtherPressrooms = false,
    selectableTypes = [MaterialType.Image, MaterialType.Video, MaterialType.Document],
  } = props;
  const title: string = props.title ?? t('publish_pr-edit_related-media_title');
  const searchPlaceholder: string = props.searchPlaceholder ?? t('materials_search_placeholder');

  const [filterMaterialType, setFilterMaterialType] = useState(selectableTypes[0]);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: pressroomListData, loading: pressroomListLoading } = useGetPressroomListQuery({
    variables: {
      sourceId,
    },
    onError: () => {
      showFailureToast(t('common_general_error'));
    },
  });

  const {
    material: { timezone, locale },
  } = useTimeSettings();

  const debouncedSetSearchTerm = useDebouncedCallback(setSearchTerm, 350, {
    maxWait: 1000,
    leading: true,
  });
  const pressroomsOptions =
    pressroomListData?.source?.pressrooms
      ?.filter(p => (onlyOtherPressrooms ? p.id !== currentPressroomId : true))
      .map(pressroom => ({
        label: `${pressroom.name} (${pressroom.siteCode})`,
        value: pressroom.id,
      })) ?? EMPTY_ARRAY;

  const [selectedPressroom, setSelectedPressroom] = useStateWithOverwrite(
    (pressroomsOptions?.find(po => po.value === currentPressroomId) ?? pressroomsOptions?.[0])
      ?.value,
  );

  const [status, setStatus] = useState<MaterialStatusFilter | null>(null);
  const [sort, setSort] = useState<MaterialListSort | null>(null);
  const [imageSort, setImageSort] = useState<ImageSort | null>(null);
  const isImageQuery = filterMaterialType === MaterialType.Image;
  const lastAskedCursorRef = useRef<string | null>(null);

  const { data, networkStatus, refetch, fetchMore } = useMediaPickerQuery({
    variables: {
      pressroomId: selectedPressroom,
      type: filterMaterialType,
      query: searchTerm,
      status,
      sort,
      first: showUploader ? 11 : 12,
      exclude:
        filterMaterialType === props.exclude?.__typename && props.exclude?.id
          ? Number(props.exclude.id)
          : undefined,
      isImageQuery,
      imageSort,
      imageStatus: materialStatusToImageStatus(status),
    },
    skip: !selectedPressroom,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onError: () => {
      showFailureToast(t('common_general_error'));
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Change any filters, and we want to reset pagination state
  useEffect(() => {
    lastAskedCursorRef.current = null;
  }, [filterMaterialType, searchTerm, selectedPressroom, status, sort, imageSort]);

  const listData = isImageQuery ? data?.pressroom?.images : data?.pressroom?.materialList;
  const materials = listData?.materials ?? EMPTY_ARRAY;
  const sourceIsTrusted = pressroomListData?.source?.trusted ?? true;

  const renderSpinner =
    pressroomListLoading ||
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables;

  const renderingList = !renderSpinner && materials.length > 0;

  const statusOptions = useMemo(
    () => [
      { value: null, label: capitalizeFirstLetter(t('common_all')) },
      {
        value: MaterialStatusFilter.Published,
        label: capitalizeFirstLetter(t('material_state_published')),
      },
      {
        value: MaterialStatusFilter.Draft,
        label: capitalizeFirstLetter(t('material_state_draft')),
      },
      {
        value: MaterialStatusFilter.Scheduled,
        label: capitalizeFirstLetter(t('material_state_scheduled')),
      },
    ],
    [t],
  );
  const sortOptions = useMemo(
    () => [
      {
        value: MaterialListSort.DescendingCreatedAt,
        label: t('material_sort_latest-added'),
      },
      {
        value: MaterialListSort.AscendingCreatedAt,
        label: t('material_sort_earliest-added'),
      },
      {
        value: MaterialListSort.DescendingName,
        label: t('material_sort_name-descending'),
      },
      {
        value: MaterialListSort.AscendingName,
        label: t('material_sort_name-ascending'),
      },
    ],
    [t],
  );

  const imageSortOptions = useMemo(
    () => [
      {
        value: ImageSort.DescendingCreatedAt,
        label: t('material_sort_latest-added'),
      },
      {
        value: ImageSort.AscendingCreatedAt,
        label: t('material_sort_earliest-added'),
      },
      {
        value: ImageSort.DescendingHeader,
        label: t('material_sort_name-descending'),
      },
      {
        value: ImageSort.AscendingHeader,
        label: t('material_sort_name-ascending'),
      },
      {
        value: ImageSort.AscendingUsage,
        label: t('material_sort_usage-ascending'),
      },
      {
        value: ImageSort.DescendingUsage,
        label: t('material_sort_usage-descending'),
      },
    ],
    [t],
  );

  const [pendingImages, setPendingImages] = useState(0);
  const [pendingVideos, setPendingVideos] = useState(0);
  const [pendingDocuments, setPendingDocuments] = useState(0);
  const pendingFiles =
    filterMaterialType === MaterialType.Image
      ? pendingImages
      : filterMaterialType === MaterialType.Video
        ? pendingVideos
        : pendingDocuments;

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const { endCursor, hasNextPage } = listData?.pageInfo ?? {};

  const [listView, setListView] = useState(false);

  const loadMore = useCallback(() => {
    if (!endCursor) {
      return;
    }
    const restoreCursor = lastAskedCursorRef.current;
    lastAskedCursorRef.current = endCursor;
    fetchMore({
      variables: {
        after: endCursor,
        first: 12,
      },
    }).catch(e => {
      console.error(e);
      showFailureToast(t('common_general_error'));
      lastAskedCursorRef.current = restoreCursor;
    });
  }, [endCursor, fetchMore, showFailureToast, t]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasNextPage) return;

    const observer = new IntersectionObserver(entries => {
      const isIntersecting = !!entries.find(entry => entry.isIntersecting);
      if (isIntersecting && lastAskedCursorRef.current !== endCursor) {
        loadMore();
      }
    });

    observer.observe(target);

    return () => {
      observer.unobserve(target);
    };
  });

  const renderFilter = (type: MediaTypes) => (
    <StyledFiltersWrapper>
      <FilterSearch
        name="freeText"
        placeholder={searchPlaceholder}
        onChange={(_, value) => {
          debouncedSetSearchTerm(value);
        }}
      />
      <Select
        label={t('material_filter_label', {
          pluralName:
            type === MaterialType.Image
              ? t('material_name_image_plural')
              : type === MaterialType.Document
                ? t('material_name_document_plural')
                : t('material_name_video_plural'),
        })}
        onChange={v => setStatus(v)}
        options={statusOptions}
        value={status}
      />
      {isImageQuery ? (
        <Select
          label={t('material_sort_label')}
          onChange={v => setImageSort(v)}
          options={imageSortOptions}
          value={imageSort}
        />
      ) : (
        <Select
          label={t('material_sort_label')}
          onChange={v => setSort(v)}
          options={sortOptions}
          value={sort}
        />
      )}
      <TabsWrapper>
        <IconTabs
          activeTab={listView ? 1 : 0}
          setActiveTab={newTabIndex => setListView(newTabIndex === 1)}
          tabs={[
            {
              icon: <IconDashboard />,
              dataTestId: 'grid-view',
            },
            {
              icon: <IconList />,
              dataTestId: 'list-view',
            },
          ]}
          variant="subTabs"
        />
      </TabsWrapper>
    </StyledFiltersWrapper>
  );

  const renderContent = () =>
    !listView ? (
      <>
        <MaterialCardGrid $asGrid={renderingList}>
          {showUploader && (
            <>
              <UploaderWrapper
                $visible={
                  showImageUploader || (renderingList && filterMaterialType === MaterialType.Image)
                }
              >
                <ImageUploaderCell
                  setPendingFiles={setPendingImages}
                  refetch={refetch}
                  asListItem={false}
                />
              </UploaderWrapper>
              <UploaderWrapper
                $visible={renderingList && filterMaterialType === MaterialType.Document}
              >
                <DocumentUploaderCell setPendingFiles={setPendingDocuments} refetch={refetch} />
              </UploaderWrapper>
              <UploaderWrapper
                $visible={renderingList && filterMaterialType === MaterialType.Video}
              >
                <VideoUploaderCell
                  setPendingFiles={setPendingVideos}
                  refetch={refetch}
                  pendingVideoId={
                    (filterMaterialType === MaterialType.Video &&
                      // page === 1 &&
                      materials.find(
                        m => m?.__typename === 'Video' && m.resourceVideo?.processed === false,
                      )?.id) ||
                    undefined
                  }
                />
              </UploaderWrapper>
            </>
          )}
          {renderSpinner ? (
            <ModalCenter>
              <Spinner size="xxl" />
            </ModalCenter>
          ) : materials.length === 0 ? (
            <EmptyStateWrapper>
              <UploaderWrapper
                $visible={showImageUploader || filterMaterialType === MaterialType.Image}
              >
                <ImageUploaderCell
                  setPendingFiles={setPendingImages}
                  refetch={refetch}
                  asListItem={false}
                />
              </UploaderWrapper>
              <UploaderWrapper $visible={filterMaterialType === MaterialType.Document}>
                <DocumentUploaderCell setPendingFiles={setPendingDocuments} refetch={refetch} />
              </UploaderWrapper>
              <UploaderWrapper $visible={filterMaterialType === MaterialType.Video}>
                <VideoUploaderCell setPendingFiles={setPendingVideos} refetch={refetch} />
              </UploaderWrapper>
            </EmptyStateWrapper>
          ) : (
            <>
              {Array.from({ length: pendingFiles }).map((_, i) => (
                <div key={`pending-${i}`}>
                  <Card>
                    <SpinnerWrapper>
                      <Spinner size="lg" />
                    </SpinnerWrapper>
                  </Card>
                </div>
              ))}
              {materials.filter(toBoolean).map(material => {
                let imageUrl: string | undefined | null;
                switch (material.__typename) {
                  case 'Image':
                    imageUrl = material.thumbnail?.url;
                    break;
                  case 'Video':
                    imageUrl = material.thumbnailUrl;
                    break;
                  case 'Document':
                    imageUrl = material.preview;
                    break;
                }
                const disabled = props.isDisabled?.(material);
                return (
                  <MediaCard
                    key={material.id}
                    title={material.header || t('common_untitled')}
                    status={material.status}
                    usageCount={'usageCount' in material ? material.usageCount : undefined}
                    onClick={() => {
                      if (disabled) return;
                      if ('onSelect' in props) {
                        props.onSelect(material);
                      }
                    }}
                    image={imageUrl ? { url: imageUrl } : undefined}
                    type={material?.__typename}
                    selectMode
                    isSelected={'isSelected' in props && props.isSelected(material)}
                    isPending={
                      ('isPending' in props && props.isPending?.(material)) ||
                      (material.__typename === 'Video' &&
                        material.resourceVideo?.processed === false)
                    }
                  />
                );
              })}
              <FillEmptySpace />
              <FillEmptySpace />
              <FillEmptySpace />
            </>
          )}
        </MaterialCardGrid>
        {hasNextPage && (
          <LoadMoreWrapper ref={loadMoreRef}>
            <Button
              data-testid="load_more_materials_btn"
              type="button"
              onClick={loadMore}
              loading={networkStatus === NetworkStatus.fetchMore}
            >
              {t('common_load_more')}
            </Button>
          </LoadMoreWrapper>
        )}
      </>
    ) : (
      <>
        <ResourceList
          items={materials.filter(toBoolean).map((material, _index) => {
            const title = (material.header || t('common_untitled')) ?? '';

            const isChecked = 'isSelected' in props && props.isSelected(material);

            const disabled = props.isDisabled?.(material);

            const status = getMaterialStatus(
              material.status === MaterialStatus.Draft,
              material.status === MaterialStatus.Scheduled,
              material.publishedAt ?? material.scheduledFor,
              timezone,
              locale,
              sourceIsTrusted,
              undefined,
              material.status === MaterialStatus.Archived,
            );

            let imageUrl: string | undefined | null;
            switch (material.__typename) {
              case 'Image':
                imageUrl = material.thumbnail?.url;
                break;
              case 'Video':
                imageUrl = material.thumbnailUrl;
                break;
              case 'Document':
                imageUrl = material.preview;
                break;
            }

            return {
              title,
              avatar: <Avatar type="thumbnail" image={imageUrl} key={imageUrl} />,
              actions:
                selectableTypes.length > 1 ? (
                  <ToggleSwitch
                    id="relatedMaterialsToggle"
                    disabled={disabled}
                    checked={isChecked}
                    data-testid={
                      isChecked ? 'related-material-toggle-active' : 'related-material-toggle'
                    }
                    onChange={() => {
                      if (disabled) return;
                      if ('onSelect' in props) {
                        props.onSelect(material);
                      }
                    }}
                  />
                ) : undefined,
              description: (
                <BadgeWrapper>
                  <Badge
                    variant={materialStatusToBadgeVariant(status.variant)}
                    text={t(status.label)}
                  />
                  {'usageCount' in material && material.usageCount ? (
                    <Tooltip
                      content={t('media_bank_usage_tooltip', { count: material.usageCount })}
                    >
                      <Badge
                        icon={<IconStory />}
                        variant="neutral"
                        text={`${t('common_used')} (${material.usageCount.toString()})`}
                      />
                    </Tooltip>
                  ) : null}
                  {'publishedTime' in status && status.publishedTime && (
                    <time>{status.publishedTime}</time>
                  )}
                </BadgeWrapper>
              ),
              onClick:
                selectableTypes.length === 1
                  ? () => {
                      if (disabled) return;
                      if ('onSelect' in props) {
                        props.onSelect(material);
                      }
                    }
                  : undefined,
            };
          })}
          renderItem={CustomItem}
          isLoading={renderSpinner}
          empty={<NoImagesFound />}
        />
        {hasNextPage && (
          <LoadMoreWrapper ref={loadMoreRef}>
            <Button
              data-testid="load_more_materials_btn"
              type="button"
              onClick={loadMore}
              loading={networkStatus === NetworkStatus.fetchMore}
            >
              {t('common_load_more')}
            </Button>
          </LoadMoreWrapper>
        )}
      </>
    );

  return (
    <>
      <Header>
        {title && (
          <Text as="h2" variant="headlineMedium" color="GREY_1000">
            {title}
          </Text>
        )}
        {props.description && (
          <Text as="p" variant="body" color="GREY_800">
            {props.description}
          </Text>
        )}
      </Header>
      <BoxBody padding={false}>
        <TabsPadding>
          {selectableTypes.length > 1 ? (
            <Tabs
              variant="mainTabs"
              fullWidth
              stickyHeader
              activeTab={
                filterMaterialType
                  ? [MaterialType.Image, MaterialType.Video, MaterialType.Document].indexOf(
                      filterMaterialType,
                    )
                  : 0
              }
              setActiveTab={newTabIndex =>
                setFilterMaterialType(
                  ([MaterialType.Image, MaterialType.Video, MaterialType.Document][
                    newTabIndex
                  ] as typeof filterMaterialType) || MaterialType.Image,
                )
              }
              tabs={selectableTypes.map(type => ({
                title: t(materialTypeLabelKeys[type]),
                dataTestId: `${type}-tab`,
                renderFilter: () => renderFilter(type),
                renderContent,
              }))}
            />
          ) : (
            <>
              <StickyWrapper>
                <div>{renderFilter(selectableTypes[0])}</div>
              </StickyWrapper>
              <div>
                <div>{renderContent()}</div>
              </div>
            </>
          )}
        </TabsPadding>
      </BoxBody>
      <BoxFooter>
        <Footer onDismiss={onDismiss}>
          <FilterSelect
            show={pressroomsOptions.length > 1}
            name="pressroom"
            label={t('common_newsroom')}
            onChange={(_, value) => {
              setSelectedPressroom(value);
            }}
            options={pressroomsOptions}
            value={selectedPressroom}
            skipMargin
            data-testid="select_newsroom"
          />
        </Footer>
      </BoxFooter>
    </>
  );
};

export const MediaPicker = ({ show, ...props }: ExternalProps) => (
  <Box
    size="lg"
    openedAsModal={show}
    onClose={props.onDismiss}
    withHeaderAndFooter={false}
    fixedHeight
  >
    <MediaPickerContent {...props} />
  </Box>
);
